import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../ui/Dialog';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';

export interface OptionType {
  value: string;
  label: string;
}

export const FALLBACK_PARAMETER_OPTIONS: OptionType[] = [
  { value: 'order.merchant.id', label: 'Order Merchant ID' },
  { value: 'order.destination.city', label: 'Order Destination City' },
  { value: 'order.destination.area', label: 'Order Destination Area' },
  { value: 'order.weight', label: 'Order Weight' },
  { value: 'order.value', label: 'Order Value' },
  { value: 'order.paymentMethod', label: 'Payment Method' },
  { value: 'order.serviceType', label: 'Service Type' },
];

export const FALLBACK_OPERATOR_OPTIONS: OptionType[] = [
  { value: 'eq', label: 'Equals (eq)' },
  { value: 'not_eq', label: 'Not Equals (not_eq)' },
  { value: 'gt', label: 'Greater Than (gt)' },
  { value: 'gte', label: 'Greater Than or Equal (gte)' },
  { value: 'lt', label: 'Less Than (lt)' },
  { value: 'lte', label: 'Less Than or Equal (lte)' },
  { value: 'contains', label: 'Contains (contains)' },
];

export const FALLBACK_CAST_OPTIONS: OptionType[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
];

interface Condition {
  parameter: string;
  operator: string;
  value: string;
  castType: string;
  id?: string;
}

interface ConditionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (condition: Condition) => void;
  initialCondition?: Condition | null;
  parameterOptions?: OptionType[];
  operatorOptions?: OptionType[];
  castOptions?: OptionType[];
}

const createDefaultCondition = (): Condition => ({
  parameter: '',
  operator: '',
  value: '',
  castType: '',
});

export const ConditionModal: React.FC<ConditionModalProps> = ({
  open,
  onOpenChange,
  onSave,
  initialCondition,
  parameterOptions = FALLBACK_PARAMETER_OPTIONS,
  operatorOptions = FALLBACK_OPERATOR_OPTIONS,
  castOptions = FALLBACK_CAST_OPTIONS,
}) => {
  const [condition, setCondition] = useState<Condition>(initialCondition || createDefaultCondition());
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setCondition(initialCondition || createDefaultCondition());
      setErrors({});
    }
  }, [initialCondition, open]);

  const resetCondition = () => {
    setCondition(initialCondition || createDefaultCondition());
    setErrors({});
  };

  const handleFieldChange = (field: keyof Condition, value: string) => {
    setCondition((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};
    if (!condition.parameter) newErrors.parameter = 'Parameter is required';
    if (!condition.operator) newErrors.operator = 'Operator is required';
    if (!condition.value) newErrors.value = 'Value is required';
    if (!condition.castType) newErrors.castType = 'Cast Type is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all required fields.');
      return;
    }

    if (condition.parameter && condition.operator && condition.value && condition.castType) {
      onSave({
        ...condition,
        castType: condition.castType,
      });
      setCondition(createDefaultCondition());
      setErrors({});
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    resetCondition();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>
            {initialCondition ? 'Edit Condition' : 'Add Condition'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Select
            label="Parameter"
            options={parameterOptions}
            value={condition.parameter}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
              handleFieldChange('parameter', e.target.value)
            }
            placeholder="Select parameter"
            required
            error={errors.parameter}
          />

          <Select
            label="Operator"
            options={operatorOptions}
            value={condition.operator}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
              handleFieldChange('operator', e.target.value)
            }
            placeholder="Select operator"
            required
            error={errors.operator}
          />

          <Input
            label="Value"
            value={condition.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('value', e.target.value)
            }
            placeholder="Enter condition value"
            required
            error={errors.value}
          />

          <Select
            label="Cast Type"
            options={castOptions}
            value={condition.castType}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
              handleFieldChange('castType', e.target.value)
            }
            placeholder="Select cast type"
            required
            error={errors.castType}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

